import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Empty } from "@/components/Empty";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { TypeBadge } from "@/pages/applications/pages/customFields/components/TypeBadge";

type CustomField = InterludeTypes["CustomField"];

interface CustomFieldTableProps {
  variant: "active" | "archived";
  fields: Array<CustomField>;
  isLoading: boolean;
  isLimitReached: boolean;
  onEdit?: (field: CustomField) => void;
  onArchive?: (field: CustomField) => void;
  onRestore?: (field: CustomField) => void;
}

const styles = {
  tableWrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  row: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  divider: {
    width: "100%",
    height: "1px",
    backgroundColor: theme.colors.neutral030,
  },
  colName: {
    flex: "0 0 25%",
    padding: "12px 16px 12px 8px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  colKey: {
    flex: "0 0 20%",
    padding: "12px 8px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  colType: {
    flex: "0 0 10%",
    padding: "12px 8px",
    minWidth: 0,
  },
  colDescription: {
    flex: 1,
    padding: "12px 8px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  colActions: {
    flex: "0 0 10%",
    padding: "0 8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    justifyContent: "flex-end",
  },
  actionBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px",
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "4px",
    background: "none",
    cursor: "pointer",
    color: theme.colors.staticFgNote,
    fontSize: "16px",
    "&:hover": {
      backgroundColor: theme.colors.staticBgPlain,
    },
  },
  actionBtnDisabled: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px",
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "4px",
    background: "none",
    cursor: "default",
    color: theme.colors.staticFgDisable,
    fontSize: "16px",
    opacity: 0.5,
  },
  restoreBtnActive: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px",
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "4px",
    background: "none",
    cursor: "pointer",
    color: theme.colors.staticFgPrimary,
    fontSize: "16px",
    "&:hover": {
      backgroundColor: theme.colors.staticBgPlain,
    },
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "60px 0",
  },
  emptyIcon: {
    fontSize: "64px",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 0",
  },
  infoIcon: {
    fontSize: "14px",
    color: theme.colors.staticFgPlaceholder,
    cursor: "help",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const CustomFieldTable: React.FC<CustomFieldTableProps> =
  function CustomFieldTable({
    variant,
    fields,
    isLoading,
    isLimitReached,
    onEdit,
    onArchive,
    onRestore,
  }) {
    const { t } = useTranslation();

    const emptyMessage = useMemo(() => {
      return variant === "active"
        ? t("customField.table.empty.active")
        : t("customField.table.empty.archived");
    }, [t, variant]);

    const renderRestoreButton = (field: CustomField): React.ReactNode => {
      if (isLimitReached) {
        return (
          <Tooltip title={t("customField.page.limitReached")} arrow>
            <Box component="span">
              <Box component="button" sx={styles.actionBtnDisabled} disabled>
                <MotifIcon un-i-motif="arrow_refresh" />
              </Box>
            </Box>
          </Tooltip>
        );
      }

      return (
        <Box
          component="button"
          sx={styles.restoreBtnActive}
          onClick={() => onRestore?.(field)}
        >
          <MotifIcon un-i-motif="arrow_refresh" />
        </Box>
      );
    };

    return (
      <Box sx={styles.tableWrapper}>
        {/* Header Row */}
        <Box sx={styles.row}>
          <Box sx={styles.colName}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("customField.table.column.name")}
            </Typography>
          </Box>
          <Box sx={styles.colKey}>
            <Stack direction="row" alignItems="center" gap="4px">
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgNote}
              >
                {t("customField.table.column.key")}
              </Typography>
              <Tooltip title={t("customField.table.keyTooltip")} arrow>
                <Box component="span" sx={styles.infoIcon}>
                  <MotifIcon un-i-motif="circle_info" />
                </Box>
              </Tooltip>
            </Stack>
          </Box>
          <Box sx={styles.colType}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("customField.table.column.type")}
            </Typography>
          </Box>
          <Box sx={styles.colDescription}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgNote}
            >
              {t("customField.table.column.description")}
            </Typography>
          </Box>
          <Box sx={styles.colActions} />
        </Box>
        <Box sx={styles.divider} />

        {/* Loading State */}
        {isLoading ? (
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {/* Empty State */}
        {!isLoading && fields.length === 0 ? (
          <Box sx={styles.emptyContainer}>
            <Box sx={styles.emptyIcon}>
              <Empty />
            </Box>
            <Typography
              variant="body"
              fontWeight={400}
              color={theme.colors.staticFgNote}
            >
              {emptyMessage}
            </Typography>
          </Box>
        ) : null}

        {/* Data Rows */}
        {!isLoading
          ? fields.map((field) => (
              <Box key={field.id}>
                <Box sx={styles.row}>
                  <Box sx={styles.colName}>
                    <Tooltip title={field.name} arrow>
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgTitle}
                      >
                        {field.name}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box sx={styles.colKey}>
                    <Tooltip title={field.key} arrow>
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgNote}
                      >
                        {field.key}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box sx={styles.colType}>
                    <TypeBadge fieldType={field.type} />
                  </Box>
                  <Box sx={styles.colDescription}>
                    <Tooltip title={field.description || "-"} arrow>
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgNote}
                      >
                        {field.description || "-"}
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box sx={styles.colActions}>
                    {variant === "active" ? (
                      <>
                        {/*
                         * A type this build does not know cannot be edited:
                         * the modal would offer name and description while
                         * silently ignoring whatever else the type has, and
                         * there is no way to know what that is. The `Unknown`
                         * badge on the row is the explanation, so the button
                         * carries no message of its own.
                         *
                         * Archive stays available either way. It is a
                         * field-level switch that needs to know nothing about
                         * the type, and the field still occupies one of the
                         * organization's lifetime slots -- blocking it would
                         * leave an administrator with no move at all.
                         */}
                        <Box
                          component="button"
                          data-test="edit-field-btn"
                          sx={
                            field.type.expected
                              ? styles.actionBtn
                              : styles.actionBtnDisabled
                          }
                          disabled={!field.type.expected}
                          onClick={() => onEdit?.(field)}
                        >
                          <MotifIcon un-i-motif="edit" />
                        </Box>
                        <Box
                          component="button"
                          data-test="archive-field-btn"
                          sx={styles.actionBtn}
                          onClick={() => onArchive?.(field)}
                        >
                          <MotifIcon un-i-motif="box" />
                        </Box>
                      </>
                    ) : (
                      renderRestoreButton(field)
                    )}
                  </Box>
                </Box>
                <Box sx={styles.divider} />
              </Box>
            ))
          : null}
      </Box>
    );
  };
